import { useState } from "react";
import StyledInput from "../common/inputs/StyledInput";
import styles from "./Results.module.css";
import StyledButton from "../common/buttons/StyledButton";
import classNames from "classnames";
import { Channel, ValidationErrors } from "../../lib/api/models";
import { useUpdatePermCollQueue } from "../../lib/api/queries";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import { ChannelMapToFull } from "../../lib/channelUtil";

const strings = {
  permCollQueue: "Perm Coll Queue",
  submit: "Submit Coll Queue",
  submitting: "Submitting",
  submitted: "Submitted",

  a0249: "A_0249 - Perm Coll Queue must be a number between 000 and 999.",
};

function formatPermCollQueue(value: string) {
  const stripped = value.replace(/\D/g, "");
  if (stripped === "") {
    return "";
  }

  const parsed = parseInt(stripped, 10);
  const padded = parsed.toString().padStart(3, "0");
  return padded;
}

type Props = {
  dwbuid: string;
  onDisplayGeneralErrors: (error: unknown) => void;
  channel: Channel;
  referenceId?: string;
};

export default function PermCollQueue({
  dwbuid,
  channel,
  referenceId,
  onDisplayGeneralErrors,
}: Props) {
  const { mutateAsync: updatePermCollQueue, isLoading: isLoading } =
    useUpdatePermCollQueue();

  const [validationMessages, setValidationMessages] =
    useState<ValidationErrors>();

  const [value, setValue] = useState("");

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const onSubmit = async () => {
    const formatted = formatPermCollQueue(value);
    if (formatted.length !== 3) {
      setValidationMessages({
        general: [strings.a0249],
      });
      return;
    }

    setValidationMessages(undefined);
    try {
      await updatePermCollQueue({
        dwbuid,
        permCollQueueId: formatted,
        referenceId,
        channel: ChannelMapToFull[channel],
      });
      setValue("");
      setHasSubmitted(true);
    } catch (err: unknown) {
      setValidationErrorsAndDisplayGeneralErrors(
        err,
        setValidationMessages,
        onDisplayGeneralErrors,
      );
    }
  };

  return (
    <fieldset className={styles.unstyledFieldset}>
      <StyledInput
        variant="horizontal"
        innerContentClassName={styles.inputContent}
        labelClassName={styles.permCollInputLabel}
        inputClassName={styles.permCollInputValue}
        label={strings.permCollQueue}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setHasSubmitted(false);
        }}
        onBlur={() => {
          setValue(formatPermCollQueue(value));
        }}
      />
      <div
        className={classNames(styles.inputContent, styles.permCollSubmitRow)}
      >
        <div className={styles.permCollInputLabel} />
        <div className={styles.permCollInputValue}>
          <StyledButton
            type="button"
            variant="secondary"
            className={classNames(styles.permCollSubmitButton)}
            disabled={hasSubmitted}
            isLoading={isLoading}
            onClick={onSubmit}
          >
            {isLoading
              ? strings.submitting
              : hasSubmitted
                ? strings.submitted
                : strings.submit}
          </StyledButton>
        </div>
      </div>
      {validationMessages && (
        <div
          className={classNames(styles.inputContent, styles.permCollSubmitRow)}
        >
          <div className={styles.permCollInputLabel} />
          <div className={classNames(styles.error, styles.permCollInputValue)}>
            {Object.values(validationMessages)
              .map((m) => m.join(" "))
              .join(" ")}
          </div>
        </div>
      )}
    </fieldset>
  );
}
